import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Collapse,
    useTheme,
    useMediaQuery,
    Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTranslation } from 'react-i18next';
import { getFormattedMarketData } from '../services/marketDataService';

interface MobileMarketTickerProps {
    isRTL?: boolean;
}

const MobileMarketTicker: React.FC<MobileMarketTickerProps> = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [marketData, setMarketData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const fetchMarketData = async () => {
            try {
                setLoading(true);
                setError(false);
                const data = await getFormattedMarketData();
                setMarketData(data);
            } catch (err) {
                console.error('Error fetching market data:', err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchMarketData();

        // Refresh every 2 minutes
        const interval = setInterval(fetchMarketData, 2 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    if (!isMobile) return null;

    const MarketItem: React.FC<{
        symbol: string;
        price: string;
        changePercent: string;
        isPositive: boolean;
    }> = ({ symbol, price, changePercent, isPositive }) => (
        <Paper
            elevation={1}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'white',
                border: `1px solid ${theme.palette.divider}`,
                mb: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {symbol}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {price}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isPositive ? (
                    <TrendingUpIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                ) : (
                    <TrendingDownIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                )}
                <Typography
                    variant="body2"
                    sx={{
                        color: isPositive ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                    }}
                >
                    {changePercent}
                </Typography>
            </Box>
        </Paper>
    );

    if (loading) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('market.loading')}
                </Typography>
            </Box>
        );
    }

    if (error || !marketData) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    {t('market.error')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Paper
                elevation={2}
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        },
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                            📈
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {t('market.lastUpdated')}
                        </Typography>
                    </Box>

                    <IconButton size="small">
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <MarketItem
                            symbol="SPX"
                            price={marketData.sp500.formattedPrice}
                            changePercent={marketData.sp500.formattedChangePercent}
                            isPositive={marketData.sp500.isPositive}
                        />

                        <MarketItem
                            symbol="IXIC"
                            price={marketData.nasdaq.formattedPrice}
                            changePercent={marketData.nasdaq.formattedChangePercent}
                            isPositive={marketData.nasdaq.isPositive}
                        />

                        <MarketItem
                            symbol="XAU"
                            price={marketData.gold.formattedPrice}
                            changePercent={marketData.gold.formattedChangePercent}
                            isPositive={marketData.gold.isPositive}
                        />

                        <MarketItem
                            symbol="BTC"
                            price={marketData.bitcoin.formattedPrice}
                            changePercent={marketData.bitcoin.formattedChangePercent}
                            isPositive={marketData.bitcoin.isPositive}
                        />

                        <MarketItem
                            symbol="ETH"
                            price={marketData.ethereum.formattedPrice}
                            changePercent={marketData.ethereum.formattedChangePercent}
                            isPositive={marketData.ethereum.isPositive}
                        />
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
};

export default MobileMarketTicker;
